/** Prosjekt-API: henting, validering og URL-bygging. Pointcloud-listen er alltid dynamisk fra API-et. */

import { trimBase } from './config';

export interface PointcloudEntry {
  id: string;
  name: string;
  key?: string;
  size?: number;
  url: string;
  /** absolutt COPC-URL (apiBase + url) */
  href: string;
  initialVisible?:boolean;
}

export interface ProjectInfo {
  projectId: string;
  projectName: string;
  customerId?: string;
  status?: string;
  horizontalCrs?: string;
  verticalCrs?: string;
  schemaVersion?: string;
  raw: Record<string, unknown>;
  pointclouds: PointcloudEntry[];
  /** oppføringer fra API-et som manglet påkrevde felt (vises som feil, stopper ikke resten) */
  invalid: { index: number; reason: string; entry: unknown }[];
}

export class ApiError extends Error {
  constructor(message: string, readonly url: string, readonly status?: number) {
    super(message);
  }
}

/** Absolutt URL: apiBase + pointcloud.url. Absolutte URL-er fra API-et brukes uendret. */
export function resolvePointcloudUrl(apiBase: string, url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  const base = trimBase(apiBase);
  return base + (url.startsWith('/') ? url : '/' + url);
}

export function projectUrl(metadataBase: string, projectId: string): string {
  return `${trimBase(metadataBase)}/api/projects/${encodeURIComponent(projectId)}`;
}

export function parseProject(json: unknown, apiBase: string): ProjectInfo {
  if (!json || typeof json !== 'object') throw new Error('Prosjektsvaret er ikke et JSON-objekt');
  const root = json as Record<string, unknown>;
  if (root.ok === false) throw new Error(`API svarte ok=false: ${String(root.error ?? 'ukjent feil')}`);
  const p = (root.project ?? root) as Record<string, unknown>;
  const list = p.pointclouds;
  if (!Array.isArray(list)) throw new Error('Prosjektet mangler pointclouds-liste');
  const seen = new Set<string>();
  const pointclouds: PointcloudEntry[] = [];
  const invalid: ProjectInfo['invalid'] = [];
  list.forEach((e, index) => {
    const o = (e ?? {}) as Record<string, unknown>;
    const id = typeof o.id === 'string' ? o.id : undefined;
    const url = typeof o.url === 'string' ? o.url : undefined;
    if (!id || !url) {
      invalid.push({ index, reason: !id ? 'mangler id' : 'mangler url', entry: e });
      return;
    }
    if (seen.has(id)) {
      invalid.push({ index, reason: `duplisert id ${id}`, entry: e });
      return;
    }
    seen.add(id);
    pointclouds.push({
      id,
      name: typeof o.name === 'string' && o.name ? o.name : id,
      key: typeof o.key === 'string' ? o.key : undefined,
      size: typeof o.size === 'number' && Number.isFinite(o.size) ? o.size : undefined,
      url,
      href: resolvePointcloudUrl(apiBase, url),
      initialVisible:typeof o.visible==='boolean'?o.visible:undefined,
    });
  });
  const str = (v: unknown) => (typeof v === 'string' ? v : undefined);
  return {
    projectId: str(p.project_id) ?? str(p.id) ?? '',
    projectName: str(p.project_name) ?? str(p.name) ?? str(p.project_id) ?? 'Uten navn',
    customerId: str(p.customer_id),
    status: str(p.status) ?? str(p.qc_status),
    horizontalCrs: str(p.horizontal_crs),
    verticalCrs: str(p.vertical_crs),
    schemaVersion: str(p.schema_version) ?? str(p.schema),
    raw: p,
    pointclouds,
    invalid,
  };
}

export async function fetchProject(metadataBase: string, apiBase: string, projectId: string, signal?: AbortSignal): Promise<ProjectInfo> {
  const url = projectUrl(metadataBase, projectId);
  let res: Response;
  try {
    res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  } catch (e) {
    throw new ApiError(`Nettverksfeil mot prosjekt-API (${(e as Error).message}). Mangler CORS på prosjekt-endepunktet?`, url);
  }
  if (!res.ok) throw new ApiError(`Prosjekt-API svarte ${res.status} ${res.statusText}`, url, res.status);
  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiError('Prosjekt-API returnerte ikke gyldig JSON', url, res.status);
  }
  return parseProject(json, apiBase);
}
